import { createContext, useContext, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import ReviewRedirect from "./pages/ReviewRedirect";
import AdminApp from "./admin/AdminApp";
import {
    MapPin,
    Phone,
    Mail,
    Instagram,
    Cake,
    Gift,
    Heart,
    Users,
    Sun,
    Pizza,
    ArrowUpRight,
    ArrowRight,
    ArrowLeft,
    Download,
    X,
    Sparkles,
} from "lucide-react";

// Load pdf.js dynamically from CDN at runtime — bundling the ESM build via
// `import * as pdfjsLib from "pdfjs-dist/.../pdf.mjs"` breaks CRA's minified
// production build ("e[i] is not a function" inside webpack bootstrap).
const PDFJS_VERSION = "4.10.38";
const PDFJS_SCRIPT_URL = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/legacy/build/pdf.min.mjs`;
const PDFJS_WORKER_URL = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/legacy/build/pdf.worker.min.mjs`;

let _pdfjsPromise = null;
function loadPdfJs() {
    if (_pdfjsPromise) return _pdfjsPromise;
    _pdfjsPromise = (async () => {
        // Dynamic ESM import of the CDN script — kept out of the webpack graph
        // by using a runtime-built specifier so CRA doesn't try to bundle it.
        const url = PDFJS_SCRIPT_URL;
        // eslint-disable-next-line no-new-func
        const mod = await new Function("u", "return import(u)")(url);
        const lib = mod.default || mod;
        if (lib && lib.GlobalWorkerOptions) {
            lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
        }
        return lib;
    })().catch((err) => {
        _pdfjsPromise = null; // allow retry
        throw err;
    });
    return _pdfjsPromise;
}

const PHONE = "030 9038365";
const PHONE_TEL = "+390309038365";
const EMAIL = "info@spaziomarconi.com";
const ADDRESS_LINE = "Viale Guglielmo Marconi 6, 25028 Verolanuova (BS)";
const RESERVATION_URL = "https://prenota.spaziomarconi.com";
const NATALE_PDF = "/assets/menu-natale.pdf";
const NATALE_SEEN_KEY = "sm_natale_popup_seen";
const INSTAGRAM_URL = "https://www.instagram.com/spazio_marconi/";
const DIRECTIONS_URL =
    "https://www.google.com/maps/search/?api=1&query=Viale+Guglielmo+Marconi+6+25028+Verolanuova+BS";
const CAREERS_MAILTO = `mailto:${EMAIL}?subject=Candidatura%20%E2%80%93%20Lavora%20con%20noi&body=Salve%2C%20vorrei%20candidarmi%20a%20Spazio%20Marconi.%20In%20allegato%20il%20mio%20CV.%0A%0ANome%20e%20cognome%3A%0APosizione%20di%20interesse%3A%0ADisponibilit%C3%A0%3A%0A%0AGrazie%2C`;

const LOGO_COMPLETO = "/assets/logo-completo-nero.png";
const LOGO_CORTO = "/assets/logo-corto-nero.png";
const PIZZA_BRUNA = "/assets/pizza-bruna.png";

const HOURS = [
    { day: "LUN", time: "12:00 — 14:30  ·  19:00 — 22:30" },
    { day: "MAR", time: "12:00 — 14:30  ·  19:00 — 22:30" },
    { day: "MER", time: "CHIUSO", closed: true },
    { day: "GIO", time: "12:00 — 14:30  ·  19:00 — 22:30" },
    { day: "VEN", time: "12:00 — 14:30  ·  19:00 — 23:30" },
    { day: "SAB", time: "19:00 — 23:30" },
    { day: "DOM", time: "12:00 — 15:00  ·  19:00 — 22:30" },
];

const MENU_BUTTONS = [
    { label: "Pranzo di Natale", pdfUrl: NATALE_PDF, festive: true },
    { label: "Menù alla carta", pdfUrl: "/assets/menu-alla-carta.pdf" },
    { label: "Domenica a pranzo", pdfUrl: "/assets/menu-domenica.pdf" },
    { label: "Pausa pranzo", pdfUrl: "/assets/menu-pausa-pranzo.pdf" },
    { label: "Speciale compagnie", pdfUrl: "/assets/menu-compagnie.pdf" },
];

const SERVICES = [
    {
        icon: Cake,
        title: "Promo compleanno",
        text: "È il tuo giorno, sei nostro ospite.",
        cue: "Scopri la promo",
        modal: "compleanno",
        href: "https://prenota.spaziomarconi.com/promo/promo-compleanno",
    },
    {
        icon: Gift,
        title: "Gift card",
        text: "Regala un'esperienza autentica da Spazio Marconi.",
        cue: "Regala ora",
        href: "https://prenota.spaziomarconi.com/regalo",
    },
    {
        icon: Heart,
        title: "Programma fedeltà",
        text: "Torna spesso, raccogli punti e guadagna premi.",
        href: null,
    },
    {
        icon: Users,
        title: "Speciale compagnie",
        text: "Tavolate, gruppi e occasioni: pensiamo a tutto noi.",
        cue: "Consulta il menù",
        href: "/assets/menu-compagnie.pdf",
    },
    {
        icon: Sun,
        title: "Pausa pranzo",
        text: "Da lunedì a venerdì: menù veloce, gustoso, fatto bene.",
        cue: "Consulta il menù",
        href: "/assets/menu-pausa-pranzo.pdf",
    },
    {
        icon: Pizza,
        title: "Domenica a pranzo",
        text: "Il pranzo della domenica, in famiglia o con gli amici.",
        cue: "Consulta il menù",
        href: "/assets/menu-domenica.pdf",
    },
];

const IG_PHOTOS = [
    { src: "/assets/sala-travi.jpg", alt: "Sala con travi in legno chiaro" },
    { src: "/assets/carne-tenera.jpg", alt: "Filetto al sangue con patate al rosmarino" },
    { src: "/assets/cantina-vini.jpg", alt: "Cantina vini a vista" },
    { src: "/assets/antipasto-domenica.jpg", alt: "Antipasto della domenica: salumi, formaggi e fritti" },
];

function TopBar() {
    return (
        <div className="topbar" data-testid="top-bar">
            <div
                className="max-w-[1500px] mx-auto flex items-center justify-between gap-3"
                style={{ padding: "10px clamp(16px, 6vw, 96px)" }}
            >
                <div className="flex items-center gap-4 sm:gap-6">
                    <Link
                        to="/"
                        className="topbar-logo"
                        data-testid="topbar-home-logo"
                        aria-label="Spazio Marconi · Torna alla home"
                    >
                        <img
                            src={LOGO_CORTO}
                            alt="Spazio Marconi"
                            className="invert-logo"
                        />
                    </Link>
                    <a
                        href={DIRECTIONS_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="topbar-link inline-flex items-center gap-2 font-display-thin text-[12px]"
                        data-testid="topbar-directions-link"
                    >
                        <MapPin size={14} strokeWidth={1.6} />
                        <span className="topbar-link-label">INDICAZIONI</span>
                    </a>
                </div>

                <div className="flex items-center gap-3 sm:gap-5">
                    <a
                        href={`tel:${PHONE_TEL}`}
                        className="topbar-link inline-flex items-center gap-2 font-display-thin text-[12px]"
                        data-testid="topbar-phone-link"
                    >
                        <Phone size={14} strokeWidth={1.6} />
                        <span className="topbar-link-label">TEL: {PHONE}</span>
                    </a>

                    <a
                        href={RESERVATION_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="topbar-cta"
                        data-testid="topbar-reserve-cta"
                    >
                        <span>Prenota un tavolo</span>
                        <ArrowUpRight size={14} strokeWidth={1.8} />
                    </a>
                </div>
            </div>
        </div>
    );
}

function Hero() {
    return (
        <header className="hero" data-testid="hero-section">
            <div
                className="max-w-[1500px] mx-auto flex flex-col items-center text-center"
                style={{
                    padding: "clamp(72px, 12vw, 160px) clamp(20px, 6vw, 96px) clamp(56px, 8vw, 110px)",
                }}
            >
                <div className="rise rise-1 eyebrow mb-8">
                    PIZZA BRUNA<sup>®</sup> e CUCINA
                </div>

                <img
                    src={LOGO_COMPLETO}
                    alt="Spazio Marconi · Pizza Bruna® e Cucina"
                    className="rise rise-2 w-full max-w-[640px] h-auto"
                    fetchpriority="high"
                    decoding="async"
                    data-testid="hero-logo"
                />

                <div className="rise rise-3 mt-10 hero-divider" />

                <p
                    className="rise rise-4 mt-8 font-display-thin text-[13px]"
                    style={{ color: "var(--ink-soft)" }}
                    data-testid="hero-location"
                >
                    VEROLANUOVA · BRESCIA
                </p>

                <p
                    className="rise rise-4 mt-4 max-w-[560px] text-[15px] leading-relaxed"
                    style={{ color: "var(--ink-soft)" }}
                >
                    Cucina di territorio, impasti a lunga lievitazione e una sala
                    che sa di casa. Benvenuto da noi.
                </p>
            </div>
        </header>
    );
}

function InfoSection() {
    const { openPdf } = usePdfViewer();
    return (
        <section
            className="section"
            id="info"
            data-testid="info-section"
            style={{ borderTop: "1px solid rgba(28,28,28,0.08)" }}
        >
            <div className="max-w-[1300px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24">
                {/* ORARI */}
                <div data-testid="hours-block">
                    <div className="eyebrow mb-3">QUANDO TROVARCI</div>
                    <h2
                        className="font-display text-4xl sm:text-5xl lg:text-6xl"
                        style={{ color: "var(--ink)" }}
                    >
                        ORARI
                    </h2>
                    <p
                        className="mt-4 max-w-md text-[15px]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        Pranzo e cena, sette giorni su sei. Per gruppi numerosi
                        consigliamo la prenotazione.
                    </p>

                    <div className="mt-10">
                        {HOURS.map((h) => (
                            <div
                                key={h.day}
                                className="hours-row"
                                data-testid={`hours-row-${h.day.toLowerCase()}`}
                            >
                                <div className="hours-day">{h.day}</div>
                                {h.closed ? (
                                    <div className="hours-closed">{h.time}</div>
                                ) : (
                                    <div className="hours-time">{h.time}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* MENU BUTTONS */}
                <div data-testid="menu-block">
                    <div className="eyebrow mb-3">LE NOSTRE PROPOSTE</div>
                    <h2
                        className="font-display text-4xl sm:text-5xl lg:text-6xl"
                        style={{ color: "var(--ink)" }}
                    >
                        MENÙ
                    </h2>
                    <p
                        className="mt-4 max-w-md text-[15px]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        Cinque carte, una sola filosofia: ingredienti veri,
                        stagione e tempo.
                    </p>

                    <div className="mt-10 grid sm:grid-cols-2 gap-4">
                        {MENU_BUTTONS.map((item, i) => {
                            const handleClick = (e) => {
                                e.preventDefault();
                                if (item.pdfUrl) {
                                    openPdf(item.pdfUrl, item.label);
                                } else {
                                    const el = document.getElementById(
                                        "menu-coming-soon",
                                    );
                                    if (el) {
                                        el.classList.add("show");
                                        setTimeout(
                                            () => el.classList.remove("show"),
                                            2400,
                                        );
                                    }
                                }
                            };
                            return (
                                <button
                                    key={item.label}
                                    type="button"
                                    onClick={handleClick}
                                    className={`menu-btn${item.festive ? " menu-btn-festive sm:col-span-2" : ""}`}
                                    data-testid={item.festive ? "menu-btn-natale" : `menu-btn-${i}`}
                                >
                                    <span className="inline-flex items-center gap-3">
                                        {item.festive && <Sparkles size={18} strokeWidth={1.6} />}
                                        {item.label}
                                    </span>
                                    <ArrowUpRight
                                        size={18}
                                        strokeWidth={1.5}
                                        className="arrow"
                                    />
                                </button>
                            );
                        })}
                    </div>
                    <div
                        id="menu-coming-soon"
                        className="menu-toast"
                        role="status"
                        aria-live="polite"
                        data-testid="menu-toast"
                    >
                        Menù in arrivo a breve
                    </div>
                </div>
            </div>
        </section>
    );
}

function PizzaBrunaSpotlight() {
    return (
        <section
            className="spotlight section"
            id="pizza-bruna"
            data-testid="pizza-bruna-section"
        >
            <div className="max-w-[1300px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                <div className="flex justify-center" data-testid="pb-logo-wrap">
                    <img
                        src={PIZZA_BRUNA}
                        alt="Pizza Bruna® · Più scura, più buona"
                        className="invert-logo w-full max-w-[420px] h-auto"
                        data-testid="pizza-bruna-logo"
                    />
                </div>

                <div>
                    <div className="eyebrow eyebrow-dark mb-4">LA NOSTRA FIRMA</div>
                    <h2
                        className="font-display text-4xl sm:text-5xl lg:text-6xl"
                        style={{ color: "#FFFFFF", lineHeight: 0.95 }}
                    >
                        PIÙ SCURA,
                        <br />
                        PIÙ BUONA.
                    </h2>

                    <p
                        className="mt-6 font-display-thin text-[13px]"
                        style={{ color: "var(--gold-soft)", letterSpacing: "0.24em" }}
                    >
                        PIZZA BRUNA<sup>®</sup> · NATA A VEROLANUOVA
                    </p>

                    <p
                        className="mt-8 max-w-[520px] text-[16px] leading-relaxed"
                        style={{ color: "rgba(255,255,255,0.88)" }}
                    >
                        Una base che si riconosce al primo sguardo.<br />
                        Farine di carattere, un morso che suona diverso.<br />
                        Non è un'altra pizza: è la nostra idea di pizza.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link
                            to="/pizza-bruna"
                            className="cta-gold-on-dark"
                            data-testid="pb-cta-discover"
                        >
                            <span>Scopri la Pizza Bruna<sup>®</sup></span>
                            <ArrowRight size={16} strokeWidth={1.6} />
                        </Link>
                        <a
                            href={RESERVATION_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="cta-outline-light"
                            data-testid="pb-cta-prenota"
                        >
                            <span>Prenota un tavolo</span>
                            <ArrowUpRight size={16} strokeWidth={1.6} />
                        </a>
                    </div>

                    <div className="mt-12 flex flex-wrap items-center gap-5 text-[12px] font-display-thin" style={{ color: "rgba(255,255,255,0.6)" }}>
                        <div>FARINA 0</div>
                        <span style={{ color: "var(--gold)" }}>·</span>
                        <div>FARRO INTEGRALE</div>
                        <span style={{ color: "var(--gold)" }}>·</span>
                        <div>FARINA DI CECI</div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function PizzeShowcase() {
    return (
        <section
            className="section pizze-showcase"
            id="pizze"
            data-testid="pizze-showcase"
        >
            <div className="max-w-[1300px] mx-auto">
                <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-16 items-end mb-14">
                    <div>
                        <div className="eyebrow mb-3">DAL FORNO</div>
                        <h2
                            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
                            style={{ color: "var(--ink)" }}
                        >
                            I PEZZI<br />FORTI.
                        </h2>
                    </div>
                    <p
                        className="text-[16px] leading-relaxed max-w-[560px]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        Due pizze che raccontano meglio di chiunque altro la nostra
                        firma: base scura, ingredienti veri, equilibrio nel piatto.
                        Un assaggio della carta che troverai a tavola.
                    </p>
                </div>

                <div className="pizze-grid">
                    <article className="pizza-card" data-testid="pizza-card-proibita">
                        <div className="pizza-card-img">
                            <img
                                src="/assets/pizza-proibita.jpg"
                                alt="Pizza Bruna® Proibita: prosciutto cotto, patate, ricotta"
                                loading="lazy"
                            />
                        </div>
                        <div className="pizza-card-body">
                            <div className="pizza-card-tag">PIZZA BIANCA</div>
                            <h3 className="pizza-card-name">PROIBITA</h3>
                            <p className="pizza-card-text">
                                <strong>In cottura:</strong> mozzarella fiordilatte,
                                patate al rosmarino.<br />
                                <strong>In uscita:</strong> prosciutto cotto al
                                forno, ricotta fresca.
                            </p>
                            <p className="pizza-card-line">
                                Si chiama Proibita perché una sola fetta non basta mai.
                            </p>
                        </div>
                    </article>

                    <article className="pizza-card" data-testid="pizza-card-gamberi">
                        <div className="pizza-card-img">
                            <img
                                src="/assets/pizza-gamberi.jpg"
                                alt="Pizza Bruna® Gamberi: gamberi crudi, stracciatella, pomodori secchi"
                                loading="lazy"
                            />
                        </div>
                        <div className="pizza-card-body">
                            <div className="pizza-card-tag">PIZZA BIANCA</div>
                            <h3 className="pizza-card-name">GAMBERI</h3>
                            <p className="pizza-card-text">
                                <strong>In cottura:</strong> crema di patate allo
                                zafferano, mozzarella fiordilatte, pomodori
                                semi-dry, pesto.<br />
                                <strong>In uscita:</strong> gamberi crudi marinati,
                                stracciatella, pinoli tostati.
                            </p>
                            <p className="pizza-card-line">
                                Pulita, fresca, decisa. Un morso che cambia ritmo.
                            </p>
                        </div>
                    </article>
                </div>

                <div className="text-center mt-14">
                    <Link
                        to="/pizza-bruna"
                        className="cta-outline"
                        data-testid="pizze-showcase-cta"
                    >
                        <span>Scopri la Pizza Bruna<sup>®</sup></span>
                        <ArrowRight size={16} strokeWidth={1.6} />
                    </Link>
                </div>
            </div>
        </section>
    );
}

function PhotoGrid() {
    return (
        <section
            className="photo-strip"
            data-testid="photo-grid-section"
        >
            <div className="photo-frame photo-frame-wide" data-testid="photo-interior">
                <img
                    src="/assets/sala-cantina.jpg"
                    alt="Sala interna di Spazio Marconi con cantina vini e scala"
                    className="photo-img"
                    loading="lazy"
                />
            </div>
        </section>
    );
}

function PromoCompleannoModal({ open, onClose, href }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = previous;
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="modal-backdrop"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="promo-compleanno-title"
            data-testid="promo-modal-backdrop"
        >
            <div
                className="modal-card"
                onClick={(e) => e.stopPropagation()}
                data-testid="promo-modal"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="modal-close"
                    aria-label="Chiudi"
                    data-testid="promo-modal-close"
                >
                    <X size={20} strokeWidth={1.6} />
                </button>

                <div className="modal-eyebrow">PROMO COMPLEANNO</div>
                <h2 id="promo-compleanno-title" className="modal-title">
                    È IL TUO COMPLEANNO?<br />
                    <span style={{ color: "var(--gold)" }}>Sei nostro ospite.</span>
                </h2>

                <p className="modal-lead">
                    Spegni le candeline da Spazio Marconi: scarica il QR code della
                    promo e <strong>la tua quota di cibo è offerta da noi</strong>.
                </p>

                <div className="modal-section">
                    <div className="modal-section-title">COME FUNZIONA</div>
                    <ul className="modal-checklist">
                        <li>
                            <span className="modal-check">✓</span>
                            <span>
                                Vieni a festeggiare con almeno{" "}
                                <strong>3 amici</strong> (minimo 4 persone al
                                tavolo).
                            </span>
                        </li>
                        <li>
                            <span className="modal-check">✓</span>
                            <span>
                                Scegli il giorno: la <strong>vigilia</strong>, il{" "}
                                <strong>giorno del compleanno</strong> o entro i{" "}
                                <strong>7 giorni successivi</strong>.
                            </span>
                        </li>
                        <li>
                            <span className="modal-check">✓</span>
                            <span>
                                A fine cena dividiamo il conto del cibo alla
                                romana, ma <strong>la tua parte la offriamo noi</strong>.
                                Tu paghi solo bevande e coperto.
                            </span>
                        </li>
                    </ul>
                </div>

                <div className="modal-note">
                    <strong>Quando non vale:</strong> il sabato. Per il resto, sei
                    il re della festa.
                </div>

                <div className="modal-cta-row">
                    <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className="cta-gold"
                        data-testid="promo-modal-download"
                        onClick={onClose}
                    >
                        <span>Scarica la promo</span>
                        <ArrowUpRight size={18} strokeWidth={1.8} />
                    </a>
                    <button
                        type="button"
                        onClick={onClose}
                        className="cta-outline"
                        data-testid="promo-modal-dismiss"
                    >
                        Forse più tardi
                    </button>
                </div>

                <p className="modal-fineprint">
                    Il compleanno è una volta l'anno. Un regalo così, te lo fa solo
                    Spazio Marconi.
                </p>
            </div>
        </div>
    );
}

function isNataleActive(now = new Date()) {
    return now < new Date(now.getFullYear(), 11, 26);
}

function NatalePreview({ onOpen }) {
    return (
        <button type="button" className="natale-preview" onClick={onOpen} data-testid="natale-popup-preview" aria-label="Apri il menù di Natale">
            <img src="/assets/menu-natale.jpg" alt="Anteprima Menù di Natale Spazio Marconi" />
        </button>
    );
}

function NatalePopup() {
    const { pathname } = useLocation();
    const { openPdf } = usePdfViewer();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (pathname !== "/" || !isNataleActive()) return undefined;
        if (window.localStorage?.getItem(NATALE_SEEN_KEY)) return undefined;
        const t = setTimeout(() => setOpen(true), 900);
        return () => clearTimeout(t);
    }, [pathname]);

    useEffect(() => {
        if (!open) return undefined;
        window.localStorage?.setItem(NATALE_SEEN_KEY, "1");
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = previous;
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!open) return null;
    const close = () => setOpen(false);
    const viewMenu = () => {
        close();
        openPdf(NATALE_PDF, "Pranzo di Natale");
    };

    return (
        <div
            className="modal-backdrop"
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-labelledby="natale-popup-title"
            data-testid="natale-popup-backdrop"
        >
            <div className="modal-card natale-card" onClick={(e) => e.stopPropagation()} data-testid="natale-popup">
                <button type="button" onClick={close} className="modal-close" aria-label="Chiudi" data-testid="natale-popup-close">
                    <X size={20} strokeWidth={1.6} />
                </button>
                <div className="modal-eyebrow">25 DICEMBRE · SPAZIO MARCONI</div>
                <h2 id="natale-popup-title" className="modal-title">
                    PRANZO DI NATALE<br />
                    <span style={{ color: "var(--gold)" }}>A tavola, insieme.</span>
                </h2>
                <p className="modal-lead">
                    Il nostro menù delle feste, pensato per chi ami. I posti sono
                    limitati: scopri il menù e prenota il tuo tavolo.
                </p>
                <div className="natale-prices" data-testid="natale-popup-prices">
                    <span><strong>Adulti 65€</strong></span>
                    <span><strong>Bambini 30€</strong> (2–10 anni)</span>
                </div>
                <NatalePreview onOpen={viewMenu} />
                <div className="modal-cta-row">
                    <button
                        type="button"
                        className="cta-gold"
                        data-testid="natale-popup-view-menu"
                        onClick={viewMenu}
                    >
                        <span>Vedi menù</span>
                        <ArrowUpRight size={18} strokeWidth={1.8} />
                    </button>
                    <a
                        href={RESERVATION_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="cta-outline"
                        data-testid="natale-popup-reserve"
                        onClick={close}
                    >
                        Prenota
                    </a>
                </div>
            </div>
        </div>
    );
}

function Services() {
    const [promoOpen, setPromoOpen] = useState(false);
    const { openPdf } = usePdfViewer();
    return (
        <section className="section" id="servizi" data-testid="services-section">
            <div className="max-w-[1300px] mx-auto">
                <div className="grid lg:grid-cols-[1fr_2fr] gap-12 mb-16 items-end">
                    <div>
                        <div className="eyebrow mb-3">COSA OFFRIAMO</div>
                        <h2
                            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
                            style={{ color: "var(--ink)" }}
                        >
                            Più di un<br />ristorante.
                        </h2>
                    </div>
                    <p
                        className="text-[16px] leading-relaxed max-w-[560px]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        Dalle compagnie alle famiglie, dai compleanni ai pranzi
                        veloci: ogni occasione ha il suo posto da noi.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-0 border-t border-l" style={{ borderColor: "rgba(28,28,28,0.15)" }}>
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        const cardStyle = {
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "1px solid rgba(28,28,28,0.15)",
                            borderBottom: "1px solid rgba(28,28,28,0.15)",
                        };
                        const cueLabel = s.cue || "Consulta il menù";
                        const Inner = (
                            <>
                                <div className="svc-icon">
                                    <Icon size={20} strokeWidth={1.6} />
                                </div>
                                <h3
                                    className="font-display text-2xl"
                                    style={{ color: "var(--ink)" }}
                                >
                                    {s.title.toUpperCase()}
                                </h3>
                                <p
                                    className="text-[14px] leading-relaxed"
                                    style={{ color: "var(--ink-soft)" }}
                                >
                                    {s.text}
                                </p>
                                {s.href || s.modal ? (
                                    <span className="svc-card-cue">
                                        {cueLabel}
                                        <ArrowUpRight size={14} strokeWidth={1.8} />
                                    </span>
                                ) : (
                                    <span className="svc-card-soon">In arrivo</span>
                                )}
                            </>
                        );
                        if (s.modal === "compleanno") {
                            return (
                                <button
                                    key={s.title}
                                    type="button"
                                    onClick={() => setPromoOpen(true)}
                                    className="svc-card svc-card-link svc-card-button"
                                    style={cardStyle}
                                    data-testid={`service-card-${i}`}
                                >
                                    {Inner}
                                </button>
                            );
                        }
                        const isPdf =
                            typeof s.href === "string" &&
                            s.href.startsWith("/assets/") &&
                            s.href.toLowerCase().endsWith(".pdf");
                        if (s.href && isPdf) {
                            return (
                                <button
                                    key={s.title}
                                    type="button"
                                    onClick={() => openPdf(s.href, s.title)}
                                    className="svc-card svc-card-link svc-card-button"
                                    style={cardStyle}
                                    data-testid={`service-card-${i}`}
                                >
                                    {Inner}
                                </button>
                            );
                        }
                        return s.href ? (
                            <a
                                key={s.title}
                                href={s.href}
                                target="_blank"
                                rel="noreferrer"
                                className="svc-card svc-card-link"
                                style={cardStyle}
                                data-testid={`service-card-${i}`}
                            >
                                {Inner}
                            </a>
                        ) : (
                            <div
                                key={s.title}
                                className="svc-card svc-card-soon-card"
                                style={cardStyle}
                                data-testid={`service-card-${i}`}
                            >
                                {Inner}
                            </div>
                        );
                    })}
                </div>
            </div>
            <PromoCompleannoModal
                open={promoOpen}
                onClose={() => setPromoOpen(false)}
                href="https://prenota.spaziomarconi.com/promo/promo-compleanno"
            />
        </section>
    );
}

function Reservation() {
    return (
        <section
            className="section"
            id="prenota"
            data-testid="reservation-section"
            style={{ background: "var(--cream-deep)" }}
        >
            <div className="max-w-[900px] mx-auto text-center">
                <div className="eyebrow mb-4">UN POSTO PER TE</div>
                <h2
                    className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
                    style={{ color: "var(--navy)" }}
                >
                    PRENOTA UN TAVOLO
                </h2>
                <p
                    className="mt-6 text-[16px] max-w-[560px] mx-auto"
                    style={{ color: "var(--ink-soft)" }}
                >
                    Assicurati il tuo posto da Spazio Marconi. La prenotazione si
                    completa in pochi secondi sul nostro portale dedicato.
                </p>

                <div className="mt-12 flex flex-col items-center gap-6">
                    <a
                        href={RESERVATION_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="cta-gold"
                        data-testid="reservation-cta-button"
                    >
                        <span>Prenota ora</span>
                        <ArrowUpRight size={18} strokeWidth={1.8} />
                    </a>

                    <div
                        className="font-display-thin text-[12px]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        OPPURE CHIAMACI AL{" "}
                        <a
                            href={`tel:${PHONE_TEL}`}
                            style={{
                                color: "var(--navy)",
                                textDecoration: "underline",
                                textUnderlineOffset: "4px",
                                textDecorationThickness: "1px",
                            }}
                            data-testid="reservation-phone-link"
                        >
                            {PHONE}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

function InstagramSection() {
    return (
        <section className="section-tight" id="instagram" data-testid="instagram-section">
            <div className="max-w-[1500px] mx-auto px-2">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
                    <div>
                        <div className="eyebrow mb-3">DIETRO LE QUINTE</div>
                        <h2
                            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
                            style={{ color: "var(--ink)" }}
                        >
                            SPAZIO MARCONI
                            <br />
                            SU INSTAGRAM
                        </h2>
                    </div>

                    <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="cta-outline self-start"
                        data-testid="instagram-follow-button"
                    >
                        <Instagram size={16} strokeWidth={1.6} />
                        <span>Seguici su Instagram</span>
                    </a>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {IG_PHOTOS.map((photo, i) => (
                        <a
                            key={photo.src}
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="ig-tile"
                            data-testid={`ig-tile-${i}`}
                        >
                            <img src={photo.src} alt={photo.alt} loading="lazy" />
                            <div className="ig-mark">@SPAZIO_MARCONI</div>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer className="footer" data-testid="footer">
            <div
                className="max-w-[1300px] mx-auto grid md:grid-cols-3 gap-12"
                style={{ padding: "clamp(56px, 7vw, 110px) clamp(20px, 6vw, 96px) 40px" }}
            >
                {/* CONTATTI */}
                <div>
                    <div
                        className="font-display-thin text-[12px] mb-5"
                        style={{ color: "var(--gold-soft)" }}
                    >
                        CONTATTI
                    </div>
                    <ul className="space-y-3 text-[14px]">
                        <li className="flex items-start gap-3">
                            <Mail size={15} className="mt-1" strokeWidth={1.6} />
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="footer-email"
                            >
                                {EMAIL}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Phone size={15} className="mt-1" strokeWidth={1.6} />
                            <a href={`tel:${PHONE_TEL}`} data-testid="footer-phone">
                                {PHONE}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <MapPin size={15} className="mt-1" strokeWidth={1.6} />
                            <a
                                href={DIRECTIONS_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-directions"
                            >
                                Verolanuova (BS), Italia
                            </a>
                        </li>
                    </ul>
                </div>

                {/* LOGO CENTRO */}
                <div className="flex flex-col items-center text-center justify-center">
                    <img
                        src={LOGO_CORTO}
                        alt="Spazio Marconi"
                        className="invert-logo w-[140px] h-auto"
                        data-testid="footer-logo"
                    />
                    <p
                        className="mt-6 font-display-thin text-[12px]"
                        style={{ color: "var(--gold-soft)" }}
                    >
                        PIZZA BRUNA<sup>®</sup> e CUCINA · VEROLANUOVA (BS)
                    </p>
                    <p
                        className="mt-2 text-[13px]"
                        style={{ color: "rgba(255,255,255,0.65)" }}
                    >
                        Pizza Bruna<sup>®</sup> · Più scura, più buona.
                    </p>
                </div>

                {/* LINKS */}
                <div className="md:text-right">
                    <div
                        className="font-display-thin text-[12px] mb-5"
                        style={{ color: "var(--gold-soft)" }}
                    >
                        ESPLORA
                    </div>
                    <ul className="space-y-3 text-[14px]">
                        <li>
                            <a
                                href={CAREERS_MAILTO}
                                data-testid="footer-link-lavora"
                            >
                                Lavora con noi
                            </a>
                        </li>
                        <li>
                            <a href="#info" data-testid="footer-link-info">
                                Info
                            </a>
                        </li>
                        <li>
                            <Link to="/privacy" data-testid="footer-link-privacy">
                                Privacy
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>

            <div
                className="max-w-[1300px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3"
                style={{
                    padding: "20px clamp(20px, 6vw, 96px)",
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.55)",
                    fontSize: "12px",
                    letterSpacing: "0.06em",
                }}
            >
                <div>© {new Date().getFullYear()} Spazio Marconi. Tutti i diritti riservati.</div>
                <div className="font-display-thin">
                    PIZZA BRUNA<sup>®</sup> È UN MARCHIO REGISTRATO
                </div>
            </div>
        </footer>
    );
}

function FloatingReserve({ visible }) {
    return (
        <a
            href={RESERVATION_URL}
            target="_blank"
            rel="noreferrer"
            className={`floating-reserve${visible ? " is-visible" : ""}`}
            data-testid="floating-reserve-cta"
            aria-label="Prenota un tavolo da Spazio Marconi"
        >
            <span className="fr-label">Prenota un tavolo</span>
            <span className="fr-label-short">Prenota</span>
            <ArrowUpRight size={16} strokeWidth={1.8} />
        </a>
    );
}

const PdfViewerContext = createContext({ openPdf: () => {} });

function usePdfViewer() {
    return useContext(PdfViewerContext);
}

function PdfViewer({ entry, onClose }) {
    const [arrayBuffer, setArrayBuffer] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [failed, setFailed] = useState(false);
    const [ready, setReady] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!entry) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = previous;
            document.removeEventListener("keydown", onKey);
        };
    }, [entry, onClose]);

    useEffect(() => {
        if (!entry) {
            setArrayBuffer(null);
            setFailed(false);
            setReady(false);
            setNumPages(0);
            return undefined;
        }
        let cancelled = false;
        setArrayBuffer(null);
        setFailed(false);
        setReady(false);
        setNumPages(0);

        fetch(entry.url, { cache: "force-cache" })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.arrayBuffer();
            })
            .then((buf) => {
                if (cancelled) return;
                setArrayBuffer(buf);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });

        return () => {
            cancelled = true;
        };
    }, [entry]);

    useEffect(() => {
        if (!arrayBuffer || !containerRef.current) return undefined;
        const container = containerRef.current;
        container.innerHTML = "";
        let cancelled = false;
        let pdfDoc = null;

        const render = async () => {
            try {
                const pdfjsLib = await loadPdfJs();
                if (cancelled) return;
                pdfDoc = await pdfjsLib.getDocument({
                    data: arrayBuffer.slice(0),
                }).promise;
                if (cancelled) return;
                setNumPages(pdfDoc.numPages);

                const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
                const maxW = Math.min(
                    1100,
                    Math.max(320, (container.clientWidth || 900) - 32),
                );

                for (let i = 1; i <= pdfDoc.numPages; i++) {
                    if (cancelled) return;
                    const page = await pdfDoc.getPage(i);
                    const base = page.getViewport({ scale: 1 });
                    const scale = maxW / base.width;
                    const viewport = page.getViewport({ scale });

                    const canvas = document.createElement("canvas");
                    canvas.className = "pdf-page-canvas";
                    canvas.width = Math.floor(viewport.width * dpr);
                    canvas.height = Math.floor(viewport.height * dpr);
                    canvas.style.width = `${Math.floor(viewport.width)}px`;
                    canvas.style.height = `${Math.floor(viewport.height)}px`;
                    container.appendChild(canvas);

                    const ctx = canvas.getContext("2d");
                    const transform =
                        dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined;
                    await page.render({
                        canvasContext: ctx,
                        viewport,
                        transform,
                    }).promise;
                }

                if (!cancelled) setReady(true);
            } catch (e) {
                if (!cancelled) setFailed(true);
            }
        };

        render();

        return () => {
            cancelled = true;
            if (pdfDoc) {
                try {
                    pdfDoc.destroy();
                } catch (_) {
                    /* ignore */
                }
            }
        };
    }, [arrayBuffer]);

    if (!entry) return null;

    return (
        <div
            className="pdf-viewer"
            role="dialog"
            aria-modal="true"
            aria-label={`Anteprima: ${entry.title || "menù"}`}
            data-testid="pdf-viewer"
        >
            <div className="pdf-viewer-bar">
                <div className="pdf-viewer-title">
                    {entry.title || "Menù"}
                    {numPages > 1 && (
                        <span className="pdf-viewer-pagecount">
                            {numPages} pagine
                        </span>
                    )}
                </div>
                <div className="pdf-viewer-actions">
                    <a
                        href={entry.url}
                        target="_blank"
                        rel="noreferrer"
                        className="pdf-viewer-btn"
                        data-testid="pdf-viewer-newtab"
                        title="Apri in una nuova scheda"
                    >
                        <ArrowUpRight size={16} strokeWidth={1.8} />
                        <span>Apri</span>
                    </a>
                    <a
                        href={entry.url}
                        download
                        className="pdf-viewer-btn"
                        data-testid="pdf-viewer-download"
                        title="Scarica il PDF"
                    >
                        <Download size={16} strokeWidth={1.8} />
                        <span>Scarica</span>
                    </a>
                    <button
                        type="button"
                        onClick={onClose}
                        className="pdf-viewer-close"
                        aria-label="Chiudi anteprima"
                        data-testid="pdf-viewer-close"
                    >
                        <X size={20} strokeWidth={1.8} />
                    </button>
                </div>
            </div>

            <div className="pdf-viewer-scroll" data-testid="pdf-viewer-scroll">
                <div className="pdf-viewer-pages" ref={containerRef} />

                {!ready && !failed && (
                    <div className="pdf-viewer-loading" data-testid="pdf-viewer-loading">
                        <div className="pdf-viewer-spinner" aria-hidden="true" />
                        <span>Apertura menù…</span>
                    </div>
                )}

                {failed && (
                    <div className="pdf-viewer-fallback" data-testid="pdf-viewer-fallback">
                        <p>
                            Anteprima non disponibile sul tuo browser.<br />
                            Usa <strong>Apri</strong> o <strong>Scarica</strong>{" "}
                            qui in alto per consultare il menù.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

function PdfViewerProvider({ children }) {
    const [entry, setEntry] = useState(null);
    const openPdf = (url, title) => {
        if (!url) return;
        setEntry({ url, title });
    };
    return (
        <PdfViewerContext.Provider value={{ openPdf }}>
            {children}
            <PdfViewer entry={entry} onClose={() => setEntry(null)} />
        </PdfViewerContext.Provider>
    );
}

function CookieBanner({ onSettled }) {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const v = localStorage.getItem("sm_cookie_consent");
        if (!v) setVisible(true);
    }, []);

    const handleChoice = (consent) => {
        localStorage.setItem("sm_cookie_consent", consent);
        setVisible(false);
        if (onSettled) onSettled();
        if (window.posthog) {
            try {
                if (consent === "accepted") window.posthog.opt_in_capturing();
                else window.posthog.opt_out_capturing();
            } catch (e) {
                /* posthog not ready yet */
            }
        }
    };

    if (!visible) return null;

    return (
        <div className="cookie-banner" role="dialog" aria-live="polite" data-testid="cookie-banner">
            <div className="cookie-banner-inner">
                <div className="cookie-text">
                    <strong>I cookie su questo sito.</strong> Usiamo cookie tecnici e
                    di analisi anonima per capire come viene usato il sito e
                    migliorare la tua esperienza. Puoi accettare oppure rifiutare i
                    cookie non essenziali.{" "}
                    <Link
                        to="/privacy"
                        className="cookie-link"
                        data-testid="cookie-privacy-link"
                    >
                        Leggi la privacy policy
                    </Link>
                </div>
                <div className="cookie-actions">
                    <button
                        type="button"
                        className="cookie-btn cookie-btn-ghost"
                        onClick={() => handleChoice("rejected")}
                        data-testid="cookie-reject"
                    >
                        Rifiuta
                    </button>
                    <button
                        type="button"
                        className="cookie-btn cookie-btn-primary"
                        onClick={() => handleChoice("accepted")}
                        data-testid="cookie-accept"
                    >
                        Accetta
                    </button>
                </div>
            </div>
        </div>
    );
}

function PrivacyPage() {
    const navigate = useNavigate();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="App" data-testid="privacy-page">
            <TopBar />
            <main
                className="section"
                style={{ maxWidth: 900, margin: "0 auto" }}
            >
                <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="back-link"
                    data-testid="privacy-back-link"
                >
                    <ArrowLeft size={16} strokeWidth={1.6} />
                    <span>Torna alla home</span>
                </button>

                <div className="eyebrow mt-8 mb-3">INFORMATIVA</div>
                <h1
                    className="font-display text-5xl sm:text-6xl"
                    style={{ color: "var(--ink)", lineHeight: 0.95 }}
                >
                    Privacy Policy
                </h1>
                <p
                    className="mt-6 text-[15px]"
                    style={{ color: "var(--ink-soft)" }}
                >
                    Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                </p>

                <div className="privacy-body">
                    <h2>Titolare del trattamento</h2>
                    <p>
                        Il titolare del trattamento dei dati è <strong>Spazio Marconi · Pizza Bruna<sup>®</sup> e Cucina</strong>,
                        {" "}{ADDRESS_LINE}. Per qualsiasi richiesta puoi scriverci a{" "}
                        <a href={`mailto:${EMAIL}`}>{EMAIL}</a> o chiamare il{" "}
                        <a href={`tel:${PHONE_TEL}`}>{PHONE}</a>.
                    </p>

                    <h2>Dati raccolti</h2>
                    <p>
                        Il sito raccoglie dati strettamente necessari al
                        funzionamento (cookie tecnici) e, previo consenso, dati di
                        navigazione anonimi tramite strumenti di analytics. Il form
                        di prenotazione è gestito dal portale esterno{" "}
                        <a href={RESERVATION_URL} target="_blank" rel="noreferrer">
                            prenota.spaziomarconi.com
                        </a>{" "}
                        e segue la sua specifica informativa.
                    </p>

                    <h2>Finalità e base giuridica</h2>
                    <ul>
                        <li>Garantire il funzionamento tecnico del sito (interesse legittimo);</li>
                        <li>Misurare in forma aggregata l'uso del sito per migliorarlo (consenso);</li>
                        <li>Rispondere a richieste inviate via email o telefono (esecuzione di un contratto / interesse legittimo);</li>
                        <li>Gestire candidature spontanee inviate a {EMAIL} (consenso del candidato).</li>
                    </ul>

                    <h2>Conservazione</h2>
                    <p>
                        I dati sono conservati per il tempo strettamente necessario
                        alle finalità sopra indicate e comunque non oltre i termini
                        previsti dalla normativa applicabile.
                    </p>

                    <h2>Diritti dell'interessato</h2>
                    <p>
                        Ai sensi degli artt. 15-22 del GDPR puoi esercitare in
                        qualsiasi momento i diritti di accesso, rettifica,
                        cancellazione, limitazione, portabilità e opposizione
                        scrivendo a <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. Hai
                        inoltre il diritto di proporre reclamo al Garante per la
                        protezione dei dati personali (
                        <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer">
                            garanteprivacy.it
                        </a>
                        ).
                    </p>

                    <h2>Cookie</h2>
                    <p>
                        Il banner cookie ti permette di accettare o rifiutare i
                        cookie non essenziali. La tua preferenza è salvata
                        localmente nel browser e può essere modificata cancellando i
                        dati del sito.
                    </p>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function PizzaBrunaPage() {
    const navigate = useNavigate();
    const { openPdf } = usePdfViewer();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="App" data-testid="pizza-bruna-page">
            <TopBar />

            {/* 1. HERO */}
            <section className="pb-hero" data-testid="pb-hero">
                <div className="pb-hero-inner">
                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        className="back-link back-link-light"
                        data-testid="pb-back-link"
                    >
                        <ArrowLeft size={16} strokeWidth={1.6} />
                        <span>Torna alla home</span>
                    </button>

                    <div className="pb-hero-grid">
                        <div>
                            <div className="eyebrow eyebrow-dark mb-5">
                                PIZZA BRUNA<sup>®</sup>
                            </div>
                            <h1
                                className="font-display"
                                style={{
                                    fontSize: "clamp(52px, 8vw, 120px)",
                                    lineHeight: 0.92,
                                    color: "#fff",
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                UNA PIZZA<br />
                                CHE SI<br />
                                <span style={{ color: "var(--gold-soft)" }}>RICONOSCE.</span>
                            </h1>
                            <p
                                className="mt-8 text-[17px] leading-relaxed max-w-[520px]"
                                style={{ color: "rgba(255,255,255,0.85)" }}
                            >
                                Più scura. Più buona. Più nostra.<br />
                                La firma dello Spazio, a Verolanuova dal primo morso.
                            </p>
                        </div>

                        <div className="pb-hero-logo-wrap">
                            <img
                                src={PIZZA_BRUNA}
                                alt="Pizza Bruna®"
                                className="invert-logo"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. IDENTITÀ */}
            <section
                className="section"
                style={{ background: "var(--cream)" }}
                data-testid="pb-identity"
            >
                <div className="pb-container pb-2col">
                    <div>
                        <div className="eyebrow mb-3">IDENTITÀ</div>
                        <h2 className="pb-h2">
                            LA FIRMA<br />DELLO SPAZIO.
                        </h2>
                    </div>
                    <div className="pb-body">
                        <p>
                            La Pizza Bruna<sup>®</sup> è la nostra. Non l'abbiamo inventata per
                            seguire una moda: l'abbiamo pensata perché la pizza, qui,
                            dovesse avere un carattere riconoscibile già prima del
                            primo morso.
                        </p>
                        <p>
                            Il colore lo vedi. Il sapore lo capisci.<br />
                            La differenza, la senti.
                        </p>
                    </div>
                </div>
            </section>

            {/* 3. IMPASTO */}
            <section
                className="section"
                style={{ background: "var(--cream-deep)" }}
                data-testid="pb-impasto"
            >
                <div className="pb-container pb-2col">
                    <div>
                        <div className="eyebrow mb-3">L'IMPASTO</div>
                        <h2 className="pb-h2">
                            TRE FARINE,<br />UNA FIRMA.
                        </h2>

                        <div className="pb-tags">
                            <span className="pb-tag">FARINA 0</span>
                            <span className="pb-tag">FARRO INTEGRALE</span>
                            <span className="pb-tag">FARINA DI CECI</span>
                        </div>
                    </div>

                    <div className="pb-body">
                        <p>
                            Ogni farina entra per una ragione, non per un'etichetta.
                            Il colore scuro non è un trucco: è quello che lasciano i
                            cereali quando hanno qualcosa da dire.
                        </p>
                        <p>
                            Il profumo ricorda il pane buono, non la pizza che hai
                            già mangiato ovunque.<br />
                            Sotto i denti è croccante fuori, arioso dentro.<br />
                            Un morso che suona diverso.
                        </p>
                    </div>
                </div>
            </section>

            {/* 4. BENEFICI */}
            <section
                className="section pb-benefits"
                data-testid="pb-benefits"
            >
                <div className="pb-container">
                    <div className="max-w-[780px]">
                        <div className="eyebrow eyebrow-dark mb-3">IL MODO GIUSTO</div>
                        <h2 className="pb-h2 pb-h2-light">
                            LEGGERA.<br />
                            NEL MODO GIUSTO.
                        </h2>
                        <p className="pb-body-light mt-8">
                            Non è una pizza “dietetica”. Non è una pizza rinuncia.<br />
                            È una pizza che finisci senza pesantezza e ti lascia
                            voglia di un'altra fetta.
                        </p>
                    </div>

                    <div className="pb-pillars">
                        <div className="pb-pillar" data-testid="pb-pillar-1">
                            <div className="pb-pillar-num">01</div>
                            <h3 className="pb-pillar-title">Digeribile davvero</h3>
                            <p className="pb-pillar-text">
                                Finisci la pizza, non la subisci. Te la ricordi per
                                il gusto, non per il dopo.
                            </p>
                        </div>
                        <div className="pb-pillar" data-testid="pb-pillar-2">
                            <div className="pb-pillar-num">02</div>
                            <h3 className="pb-pillar-title">Più fibre, più ritmo</h3>
                            <p className="pb-pillar-text">
                                Cereali veri dentro l'impasto. Ogni fetta ha il suo
                                passo, mai monotono.
                            </p>
                        </div>
                        <div className="pb-pillar" data-testid="pb-pillar-3">
                            <div className="pb-pillar-num">03</div>
                            <h3 className="pb-pillar-title">Equilibrio nel piatto</h3>
                            <p className="pb-pillar-text">
                                Una base che regge ingredienti importanti senza
                                coprirli. Il piacere resta intero.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. ESPERIENZA — collegato al menu */}
            <section
                className="section"
                style={{ background: "var(--cream)" }}
                data-testid="pb-experience"
            >
                <div className="pb-container">
                    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20 items-start mb-14">
                        <div>
                            <div className="eyebrow mb-3">COSA CI METTIAMO SOPRA</div>
                            <h2 className="pb-h2">
                                IN COTTURA.<br />IN USCITA.
                            </h2>
                        </div>
                        <p className="pb-body" style={{ alignSelf: "center" }}>
                            Una base così chiede ingredienti che sappiano dialogare.
                            Ogni pizza nasce in due tempi: quello che entra nel forno
                            e quello che arriva sopra solo alla fine. Per rispettare
                            il profumo, il colore, il contrasto.
                        </p>
                    </div>

                    <div className="pb-dishes">
                        <div className="pb-dish" data-testid="pb-dish-zucca">
                            <img src="/assets/pizza-zucca.jpg" alt="Pizza Zucca" />
                            <div className="pb-dish-body">
                                <h3 className="pb-dish-name">ZUCCA</h3>
                                <p className="pb-dish-in">
                                    <strong>In cottura:</strong> crema di zucca,
                                    mozzarella fiordilatte, gorgonzola, porcini
                                </p>
                                <p className="pb-dish-in">
                                    <strong>In uscita:</strong> rosmarino fresco
                                </p>
                            </div>
                        </div>

                        <div className="pb-dish" data-testid="pb-dish-carciofo">
                            <img src="/assets/pizza-carciofo.jpg" alt="Pizza Carciofo Tonnato" loading="lazy" />
                            <div className="pb-dish-body">
                                <h3 className="pb-dish-name">CARCIOFO TONNATO</h3>
                                <p className="pb-dish-in">
                                    <strong>In cottura:</strong> crema di carciofi,
                                    mozzarella fiordilatte
                                </p>
                                <p className="pb-dish-in">
                                    <strong>In uscita:</strong> tacchino arrosto,
                                    carciofi, tonnata, capperi
                                </p>
                            </div>
                        </div>

                        <div className="pb-dish" data-testid="pb-dish-nerano">
                            <img src="/assets/pizza-nerano.jpg" alt="Pizza Nerano con zucchine e menta" loading="lazy" />
                            <div className="pb-dish-body">
                                <h3 className="pb-dish-name">NERANO</h3>
                                <p className="pb-dish-in">
                                    <strong>In cottura:</strong> crema di zucchine,
                                    mozzarella fiordilatte, provolone del Monaco
                                </p>
                                <p className="pb-dish-in">
                                    <strong>In uscita:</strong> zucchine alla
                                    Scapece, menta, basilico, cialde di Parmigiano
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="text-center mt-12">
                        <a
                            href="/assets/menu-alla-carta.pdf"
                            target="_blank"
                            rel="noreferrer"
                            className="cta-outline"
                            data-testid="pb-cta-menu"
                        >
                            <span>Sfoglia il menù completo</span>
                            <ArrowUpRight size={16} strokeWidth={1.6} />
                        </a>
                    </div>
                </div>
            </section>

            {/* 6. POSIZIONAMENTO */}
            <section
                className="section pb-stance"
                data-testid="pb-stance"
            >
                <div className="pb-container text-center">
                    <div className="eyebrow eyebrow-dark mb-6">IL PUNTO</div>
                    <h2
                        className="font-display"
                        style={{
                            fontSize: "clamp(36px, 5.5vw, 82px)",
                            lineHeight: 1.02,
                            color: "#fff",
                            maxWidth: "1000px",
                            margin: "0 auto",
                        }}
                    >
                        NON È UNA VARIANTE.<br />
                        <span style={{ color: "var(--gold-soft)" }}>
                            È LA NOSTRA IDEA DI PIZZA.
                        </span>
                    </h2>
                    <p
                        className="mt-8 text-[16px] max-w-[620px] mx-auto"
                        style={{ color: "rgba(255,255,255,0.78)" }}
                    >
                        La Pizza Bruna<sup>®</sup> è per chi riconosce la differenza quando ce
                        l'ha davanti. Per un pubblico curioso, contemporaneo,
                        affamato.
                    </p>
                </div>
            </section>

            {/* 7. CTA FINALE */}
            <section
                className="section"
                style={{ background: "var(--cream-deep)" }}
                data-testid="pb-cta-final"
            >
                <div className="max-w-[900px] mx-auto text-center">
                    <div className="eyebrow mb-4">PROVALA A VEROLANUOVA</div>
                    <h2
                        className="font-display text-5xl sm:text-6xl lg:text-7xl"
                        style={{ color: "var(--navy)", lineHeight: 0.95 }}
                    >
                        VIENI A<br />MORDERLA.
                    </h2>
                    <p
                        className="mt-6 text-[16px] max-w-[560px] mx-auto"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        Spazio Marconi · Viale Guglielmo Marconi 6, Verolanuova (BS).<br />
                        Prenota un tavolo: ci pensiamo noi al resto.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                            href={RESERVATION_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="cta-gold"
                            data-testid="pb-cta-book"
                        >
                            <span>Prenota un tavolo</span>
                            <ArrowUpRight size={18} strokeWidth={1.8} />
                        </a>
                        <a
                            href={`tel:${PHONE_TEL}`}
                            className="cta-outline"
                            data-testid="pb-cta-call"
                        >
                            <Phone size={16} strokeWidth={1.6} />
                            <span>{PHONE}</span>
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

const TA_REVIEWS = [
    {
        name: "Luca B.",
        title: "Una garanzia",
        score: 5,
        text: "Sempre una garanzia, cibo ottimo, servizio impeccabile e atmosfera molto piacevole. Consigliatissimo per qualsiasi occasione, dalla cena romantica alla serata tra amici.",
    },
    {
        name: "Giada M.",
        title: "Ottima scoperta!",
        score: 5,
        text: "Posto moderno, curato e molto pulito. Abbiamo mangiato benissimo, dagli antipasti al dolce. Personale giovane, gentile e preparato. Torneremo sicuramente!",
    },
    {
        name: "Elena V.",
        title: "Bellissimo locale",
        score: 5,
        text: "Locale molto bello e curato nei dettagli. Il menù offre piatti ricercati e presentati molto bene. Personale molto cordiale e disponibile. Prezzi adeguati alla qualità offerta.",
    },
    {
        name: "Riccardo F.",
        title: "Eccellente",
        score: 5,
        text: "Tutto perfetto. Dalla location al servizio, per non parlare della qualità dei piatti. Un'esperienza sensoriale completa. Bravissimi!",
    },
    {
        name: "Simona T.",
        title: "Pizza superlativa",
        score: 5,
        text: "Ho mangiato una delle pizze più buone di sempre. Impasto leggero e ingredienti ricercati. Il locale è splendido, ideale per una serata speciale.",
    },
    {
        name: "Matteo L.",
        title: "Compleanno indimenticabile",
        score: 5,
        text: "Abbiamo festeggiato qui un compleanno e siamo rimasti estasiati. Servizio attento e veloce, piatti presentati come opere d'arte e sapori unici.",
    },
    {
        name: "Davide G.",
        title: "Professionalità e gusto",
        score: 5,
        text: "Si vede che dietro c'è tanto lavoro e passione. Personale molto preparato anche sui vini. Piatti equilibrati e materie prime d'eccellenza.",
    },
    {
        name: "Francesco P.",
        title: "Top a Verolanuova",
        score: 5,
        text: "Senza dubbio il miglior locale della zona. Atmosfera internazionale e cucina di alto livello. Non delude mai, consigliatissimo.",
    },
    {
        name: "Valentina S.",
        title: "Aperitivo e cena al top",
        score: 5,
        text: "Ottimi cocktail per iniziare e una cena che ha superato le aspettative. Location moderna e molto cool. Torneremo spesso.",
    },
    {
        name: "Michele N.",
        title: "Qualità costante",
        score: 5,
        text: "Frequento questo posto regolarmente e la qualità è sempre altissima. Una certezza per chi cerca il meglio in provincia di Brescia.",
    },
];

function TaBubbles({ score = 5, size = 18, label }) {
    const bubbles = [];
    for (let i = 1; i <= 5; i++) {
        const filled = i <= Math.floor(score);
        const partial = !filled && i - score < 1 && i - score > 0;
        bubbles.push(
            <span
                key={i}
                className={`ta-bubble ${filled ? "is-filled" : partial ? "is-partial" : "is-empty"}`}
                style={{ width: size, height: size }}
                aria-hidden="true"
            />
        );
    }
    return (
        <span
            className="ta-bubbles"
            role="img"
            aria-label={label || `${score} su 5 pallini Tripadvisor`}
        >
            {bubbles}
        </span>
    );
}

function ReviewsMarquee() {
    const wrapRef = useRef(null);
    const trackRef = useRef(null);
    const offsetRef = useRef(0);
    const halfRef = useRef(0);
    const rafRef = useRef(null);
    const pausedRef = useRef(false);
    // Duplicate the list so the auto-scroll loops seamlessly
    const loop = [...TA_REVIEWS, ...TA_REVIEWS];

    const measure = () => {
        const track = trackRef.current;
        if (!track) return;
        // The track holds 2x the reviews — half-width is one full pass
        halfRef.current = track.scrollWidth / 2;
    };

    const apply = () => {
        const track = trackRef.current;
        if (!track) return;
        // Wrap offset within [0, half) so the loop is seamless
        const half = halfRef.current || 1;
        let off = offsetRef.current % half;
        if (off < 0) off += half;
        offsetRef.current = off;
        track.style.transform = `translate3d(${-off}px, 0, 0)`;
    };

    useEffect(() => {
        measure();
        apply();
        const onResize = () => {
            measure();
            apply();
        };
        window.addEventListener("resize", onResize);

        const reduced = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        let last = performance.now();
        const speed = 45; // px/sec — visibly moving
        const tick = (now) => {
            const dt = (now - last) / 1000;
            last = now;
            if (!reduced && !pausedRef.current) {
                offsetRef.current += speed * dt;
                apply();
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener("resize", onResize);
        };
    }, []);

    const pause = () => {
        pausedRef.current = true;
    };
    const resume = () => {
        pausedRef.current = false;
    };

    const step = (dir) => {
        const track = trackRef.current;
        const card = track?.querySelector(".ta-review-card");
        const stepPx = card ? card.getBoundingClientRect().width + 20 : 340;
        offsetRef.current += dir * stepPx;
        apply();
    };

    return (
        <div
            className="ta-marquee-wrap"
            ref={wrapRef}
            data-testid="tripadvisor-reviews-marquee"
        >
            <button
                type="button"
                className="ta-marquee-nav ta-marquee-nav--prev"
                onClick={() => step(-1)}
                aria-label="Recensione precedente"
                data-testid="reviews-prev"
            >
                <ArrowLeft size={18} strokeWidth={1.8} />
            </button>

            <div
                className="ta-marquee-viewport"
                onMouseEnter={pause}
                onMouseLeave={resume}
                onTouchStart={pause}
                onTouchEnd={resume}
            >
                <div
                    ref={trackRef}
                    className="ta-marquee-track"
                    aria-label="Recensioni dei nostri ospiti"
                >
                    {loop.map((r, idx) => (
                        <article
                            key={`${r.name}-${idx}`}
                            className="ta-review-card"
                            data-testid={`review-card-${idx}`}
                        >
                            <header className="ta-review-head">
                                <TaBubbles score={r.score} size={14} />
                                <span className="ta-review-source">
                                    Tripadvisor
                                </span>
                            </header>
                            <h4 className="ta-review-title">{r.title}</h4>
                            <p className="ta-review-text">
                                &ldquo;{r.text}&rdquo;
                            </p>
                            <footer className="ta-review-foot">
                                — {r.name}
                            </footer>
                        </article>
                    ))}
                </div>
            </div>

            <button
                type="button"
                className="ta-marquee-nav ta-marquee-nav--next"
                onClick={() => step(1)}
                aria-label="Recensione successiva"
                data-testid="reviews-next"
            >
                <ArrowRight size={18} strokeWidth={1.8} />
            </button>
        </div>
    );
}

function TripadvisorBand() {
    return (
        <section className="ta-band" data-testid="tripadvisor-section">
            <div className="ta-band-inner">
                <div className="ta-band-text">
                    <div className="eyebrow mb-3">CI HAI SCELTO. GRAZIE.</div>
                    <h2
                        className="font-display"
                        style={{
                            fontSize: "clamp(30px, 3.6vw, 44px)",
                            lineHeight: 1.05,
                            color: "var(--ink)",
                        }}
                    >
                        TRAVELLERS' CHOICE 2023 · 2024 · 2025 · 2026
                    </h2>
                    <p
                        className="mt-4 text-[15px] leading-relaxed max-w-[460px]"
                        style={{ color: "var(--ink-soft)" }}
                    >
                        Tra i ristoranti più apprezzati della Bassa Bresciana secondo
                        chi ci ha già provato. Grazie, a chi ha scritto una
                        recensione e a chi ci ha semplicemente consigliato.
                    </p>

                    <div
                        className="ta-rating-summary"
                        data-testid="tripadvisor-rating-summary"
                    >
                        <span className="ta-rating-score">5,0</span>
                        <TaBubbles score={5} size={22} label="5 su 5 pallini Tripadvisor" />
                        <span className="ta-rating-meta">
                            su Tripadvisor
                        </span>
                    </div>

                    <a
                        href="https://www.tripadvisor.it/Restaurant_Review-g1583997-d24156082-Reviews-Spazio_Marconi-Verolanuova_Province_of_Brescia_Lombardy.html"
                        target="_blank"
                        rel="noreferrer"
                        className="ta-band-link mt-4"
                        data-testid="tripadvisor-review-link"
                    >
                        Leggi le recensioni su Tripadvisor
                        <ArrowUpRight size={14} strokeWidth={1.6} />
                    </a>
                </div>

                <div className="ta-band-badge">
                    <ul className="ta-badges-grid">
                        {[2023, 2024, 2025, 2026].map((year) => (
                            <li key={year} className="ta-badges-item">
                                <a
                                    href="https://www.tripadvisor.it/Restaurant_Review-g1583997-d24156082-Reviews-Spazio_Marconi-Verolanuova_Province_of_Brescia_Lombardy.html"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={`Tripadvisor Travellers' Choice ${year}`}
                                    data-testid={`ta-badge-${year}`}
                                >
                                    <img
                                        src={`https://static.tacdn.com/img2/travelers_choice/widgets/tchotel_${year}_LL.png`}
                                        alt={`Tripadvisor Travellers' Choice ${year}`}
                                        loading="lazy"
                                    />
                                    <span className="ta-badges-year">{year}</span>
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <ReviewsMarquee />
        </section>
    );
}

function HomePage() {
    return (
        <div className="App">
            <TopBar />
            <Hero />
            <InfoSection />
            <PizzaBrunaSpotlight />
            <PizzeShowcase />
            <PhotoGrid />
            <Services />
            <TripadvisorBand />
            <Reservation />
            <InstagramSection />
            <Footer />
        </div>
    );
}

function App() {
    const [cookieSettled, setCookieSettled] = useState(
        typeof window !== "undefined" &&
            !!window.localStorage?.getItem("sm_cookie_consent"),
    );

    return (
        <BrowserRouter>
            <PdfViewerProvider>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/pizza-bruna" element={<PizzaBrunaPage />} />
                    <Route path="/recensione" element={<ReviewRedirect />} />
                    <Route path="/admin/*" element={<AdminApp />} />
                </Routes>
                <AppChrome
                    cookieSettled={cookieSettled}
                    onCookieSettled={() => setCookieSettled(true)}
                />
            </PdfViewerProvider>
        </BrowserRouter>
    );
}

// Renders the floating reserve CTA and the cookie banner only on the public
// marketing pages. Hidden on /admin (private dashboard) and /recensione
// (transient redirect page).
function AppChrome({ cookieSettled, onCookieSettled }) {
    const { pathname } = useLocation();
    const isPublicMarketing =
        !pathname.startsWith("/admin") && !pathname.startsWith("/recensione");
    if (!isPublicMarketing) return null;
    return (
        <>
            <FloatingReserve visible={cookieSettled} />
            <CookieBanner onSettled={onCookieSettled} />
            <NatalePopup />
        </>
    );
}

export default App;
